'use client';

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

const ANNOUNCEMENT_DURATION_MS = 5000;

interface LiveRegionContextType {
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
}

const LiveRegionContext = createContext<LiveRegionContextType | null>(null);

export function useLiveRegion() {
  const context = useContext(LiveRegionContext);
  if (!context) {
    throw new Error('useLiveRegion must be used within a LiveRegionProvider');
  }
  return context;
}

interface LiveRegionProviderProps {
  children: ReactNode;
}

function useAnnouncement() {
  const [message, setMessage] = useState('');
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);

  const trigger = useCallback((text: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setMessage('');
    requestAnimationFrame(() => {
      setMessage(text);
      timeoutRef.current = setTimeout(() => setMessage(''), ANNOUNCEMENT_DURATION_MS);
    });
  }, []);

  return { message, trigger };
}

export function LiveRegionProvider({ children }: LiveRegionProviderProps) {
  const polite = useAnnouncement();
  const assertive = useAnnouncement();

  const announce = useCallback(
    (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      (priority === 'assertive' ? assertive : polite).trigger(message);
    },
    [polite, assertive],
  );

  const contextValue = useMemo(() => ({ announce }), [announce]);

  return (
    <LiveRegionContext.Provider value={contextValue}>
      {children}
      <div role="status" aria-live="polite" aria-atomic="true" className={cn('sr-only')}>
        {polite.message}
      </div>
      <div role="alert" aria-live="assertive" aria-atomic="true" className={cn('sr-only')}>
        {assertive.message}
      </div>
    </LiveRegionContext.Provider>
  );
}
